import { CpfRevelavel, DataTable, Pill, fmtBRL, fmtDataISO, type Column } from "@atlas/ui/web";
import type {
  AdminBateCarteiraLinha,
  AdminBateCarteiraResultado,
  BateCarteiraAtualidade,
  BateCarteiraForaDaCarteira,
} from "@atlas/sdk";
import type { StatusNaFolha } from "@atlas/types";
import {
  PILL_STATUS_NA_FOLHA,
  ROTULO_ORIGEM,
  ROTULO_STATUS_NA_FOLHA,
  STATUS_NA_FOLHA,
  parcelaDa,
  resultadoFolhaDa,
  statusFolhaDa,
} from "../lib/bate-carteira";

// As partes do BATE DE CARTEIRA que o banco e a averbadora mostram igual (#347):
// cards por status, quao atual e' o relatorio, as colunas da tabela e o que
// ficou fora da carteira. O que difere entre as duas telas — quem escolhe o
// banco, e se a pagina vem do servidor — fica em cada uma.

const vazio = <span style={{ color: "var(--text-dim)" }}>—</span>;
const reais = (v: number | null | undefined) => (v == null ? vazio : fmtBRL(v));

function fmtDataHora(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fmtDataISO(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** As colunas da carteira, as mesmas nas duas telas. */
export function colunasDaCarteira(cpfsRevelados: boolean): Column<AdminBateCarteiraLinha>[] {
  return [
    {
      key: "statusFolha",
      header: "Status na folha",
      render: (l) => {
        const s = statusFolhaDa(l);
        return <Pill variant={PILL_STATUS_NA_FOLHA[s]}>{ROTULO_STATUS_NA_FOLHA[s]}</Pill>;
      },
    },
    { key: "adfBanco", header: "ADF", mono: true },
    { key: "matricula", header: "Matrícula" },
    { key: "servidorNome", header: "Servidor", render: (l) => l.servidorNome || vazio },
    { key: "cpfMasked", header: "CPF", render: (l) => <CpfRevelavel cpf={l.cpf} cpfMasked={l.cpfMasked} revelado={cpfsRevelados} /> },
    { key: "prefeituraNome", header: "Órgão/entidade" },
    { key: "origemContrato", header: "Origem", render: (l) => ROTULO_ORIGEM[l.origemContrato] },
    { key: "situacao", header: "Situação" },
    { key: "parcelaNumero", header: "Parcela", align: "right", render: (l) => parcelaDa(l) ?? vazio },
    { key: "valorInstruido", header: "Instruído", align: "right", render: (l) => fmtBRL(l.valorInstruido) },
    { key: "valorDescontado", header: "Descontado", align: "right", render: (l) => reais(l.valorDescontado) },
    { key: "residuo", header: "Resíduo", align: "right", render: (l) => reais(l.residuo) },
    { key: "motivo", header: "Motivo", render: (l) => l.motivo || vazio },
  ];
}

/**
 * Total de contratos e um card por status — clicar filtra a tabela por ele,
 * clicar de novo tira o filtro. Os numeros sao do relatorio INTEIRO, com ou
 * sem filtro: e' o que a conciliacao confere.
 */
export function CardsPorStatus({
  resultado,
  statusAtivo,
  onStatus,
}: {
  resultado: AdminBateCarteiraResultado;
  statusAtivo: StatusNaFolha | null;
  onStatus: (s: StatusNaFolha | null) => void;
}) {
  const { resumo } = resultado;
  const alerta: Record<StatusNaFolha, boolean> = {
    aplicada: false,
    parcial: true,
    nao_descontada: true,
    suspensa: true,
    aguardando_retorno: false,
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <Card label="Contratos" valor={resultado.totalLinhas.toLocaleString("pt-BR")} ativo={statusAtivo === null} onClick={() => onStatus(null)} />
        {STATUS_NA_FOLHA.map((s) => {
          const n = resumo.porStatus[s] ?? 0;
          return (
            <Card
              key={s}
              label={ROTULO_STATUS_NA_FOLHA[s]}
              valor={n.toLocaleString("pt-BR")}
              alerta={alerta[s] && n > 0}
              ativo={statusAtivo === s}
              onClick={() => onStatus(statusAtivo === s ? null : s)}
            />
          );
        })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <Card label="Instruído" valor={fmtBRL(resumo.somaInstruida)} />
        <Card label="Descontado" valor={fmtBRL(resumo.somaDescontada)} />
        <Card label="Resíduo" valor={fmtBRL(resumo.somaResiduo)} alerta={resumo.somaResiduo > 0} />
        <Card label="Saldo devedor" valor={fmtBRL(resultado.somaSaldoDevedor)} />
      </div>
    </div>
  );
}

function Card({
  label,
  valor,
  alerta,
  ativo,
  onClick,
}: {
  label: string;
  valor: string;
  alerta?: boolean;
  ativo?: boolean;
  onClick?: () => void;
}) {
  const conteudo = (
    <>
      <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6, color: alerta ? "var(--gold-500)" : "var(--text)" }}>{valor}</div>
    </>
  );
  const estilo = {
    background: "var(--bg-elev)",
    border: `1px solid ${ativo ? "var(--accent)" : "var(--border-strong)"}`,
    borderRadius: 12,
    padding: "14px 16px",
    textAlign: "left" as const,
  };
  if (!onClick) return <div style={estilo}>{conteudo}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={ativo} style={{ ...estilo, cursor: "pointer", font: "inherit" }}>
      {conteudo}
    </button>
  );
}

/** Quando o retorno foi processado e o movimento emitido, por órgão/entidade. */
export function AtualidadeDoRelatorio({ atualidade }: { atualidade: BateCarteiraAtualidade[] }) {
  if (atualidade.length === 0) return null;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 12, color: "var(--text-muted)", display: "flex", flexDirection: "column", gap: 2 }}>
      {atualidade.map((a) => (
        <li key={a.unidadeId}>
          {atualidade.length > 1 ? <b style={{ color: "var(--text)" }}>{a.unidadeNome || `Órgão/entidade ${a.unidadeId}`}: </b> : null}
          {a.retornoProcessadoEm ? `Retorno processado em ${fmtDataHora(a.retornoProcessadoEm)}` : "Sem retorno da folha nesta competência"}
          {" · "}
          {a.movimentoEmitidoEm ? `movimento emitido em ${fmtDataHora(a.movimentoEmitidoEm)}` : "movimento ainda não emitido"}
        </li>
      ))}
    </ul>
  );
}

/**
 * Resultado da folha cujo contrato nao e' (mais) da carteira — pedido expirado,
 * cancelado, ADF desconhecida. Fica recolhido e fora do total: o banco precisa
 * ver, mas nao e' contrato dele.
 */
export function ForaDaCarteira({ itens, cpfsRevelados }: { itens: BateCarteiraForaDaCarteira[]; cpfsRevelados: boolean }) {
  if (itens.length === 0) return null;
  const colunas: Column<BateCarteiraForaDaCarteira>[] = [
    { key: "adf", header: "ADF", mono: true },
    { key: "matricula", header: "Matrícula" },
    { key: "servidorNome", header: "Servidor", render: (l) => l.servidorNome || vazio },
    { key: "cpfMasked", header: "CPF", render: (l) => (l.cpf || l.cpfMasked ? <CpfRevelavel cpf={l.cpf} cpfMasked={l.cpfMasked} revelado={cpfsRevelados} /> : vazio) },
    { key: "prefeituraNome", header: "Órgão/entidade" },
    { key: "situacaoContrato", header: "Situação do contrato", render: (l) => l.situacaoContrato ?? "Não encontrado" },
    { key: "resultadoFolha", header: "Resultado na folha", render: (l) => resultadoFolhaDa(l) },
    { key: "valorInstruido", header: "Instruído", align: "right", render: (l) => fmtBRL(l.valorInstruido) },
    { key: "valorDescontado", header: "Descontado", align: "right", render: (l) => fmtBRL(l.valorDescontado) },
    { key: "motivo", header: "Motivo", render: (l) => l.motivo || vazio },
  ];
  return (
    <details style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: "12px 16px" }}>
      <summary style={{ cursor: "pointer", fontWeight: 600 }}>
        Fora da carteira ({itens.length}) — resultados da folha sem contrato ativo, fora do total
      </summary>
      <div style={{ marginTop: 12 }}>
        <DataTable columns={colunas} rows={itens} rowKey={(l) => l.adf} />
      </div>
    </details>
  );
}
